import { FC, ReactElement, ReactNode } from 'react';

import { IconName } from '@purpur/library/icon';

export type NegativeAttr = { $negative: boolean };
export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'none';

export namespace PlanCardModel {
    export type NegativeProp = { isNegative: boolean };
    export type PassableComponent = FC<NegativeProp>;

    export type CapVariant = 'dark-purple' | 'light-purple' | 'red' | 'green' | 'black';
    export type CapProps = { text: string; variant: CapVariant; icon?: IconName; image?: string } | null;

    export type BadgeVariant = 'attention' | 'neutral';
    export type BadgeProps = { text: string; variant?: BadgeVariant } | null;

    export type ModalData = { content: ReactNode; imageUrl?: string; imageAlt?: string };
    export type Description = { term: string; detail: string; id: string; modal?: ModalData };

    export type Detail =
        | { text: string; available: false; id: string; modal?: never }
        | { text: string; available: true; id: string; modal: ModalData };

    export type DownloadLink = { text: string; href: string; download: string };
    export type ContractSummaryType = DownloadLink | PassableComponent;
    export type CallToActionProps = {
        text: string;
        onClick: (node: HTMLButtonElement) => void;
        disabled?: boolean;
        loading?: boolean;
    };
    export type CallToActionType = CallToActionProps | PassableComponent | null;

    export interface CardProps extends NegativeProp {
        cap: CapProps;
        header: {
            title: string;
            oldTitle?: string;
            subtitle: string;
            subtitleTag?: HeadingLevel;
            badge: BadgeProps | undefined;
        };
        body: {
            details: Detail[];
            campaign?: (negative: boolean) => ReactElement | null;
            oldPrice: string | number;
            price: string | number;
            contractSummary: ContractSummaryType;
            callToAction: CallToActionType;
        };
        footer: {
            title: string;
            descriptions: Description[];
            isForcedExpanded?: boolean;
            onExpandChange?: (isExpanded: boolean) => void;
        };
    }
}

export const isDownloadLink = (obj: PlanCardModel.ContractSummaryType): obj is PlanCardModel.DownloadLink =>
    typeof obj === 'object' && obj !== null && 'href' in obj && 'text' in obj && 'download' in obj;

export const isCallToActionProps = (obj: PlanCardModel.CallToActionType): obj is PlanCardModel.CallToActionProps =>
    typeof obj === 'object' && obj !== null && 'text' in obj && 'onClick' in obj;
